
import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfileViews } from '@/hooks/useProfileViews';
import { debugLogger } from '@/utils/debugLogger';

// Helper function to validate UUID
const isValidUUID = (str?: string): boolean => {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const useProfileViewTracker = (profileUserId?: string) => {
  const { user } = useAuth();
  const { recordProfileView } = useProfileViews();

  useEffect(() => {
    const trackView = async () => {
      // Validação rigorosa de parâmetros
      if (!user?.id) {
        debugLogger.debug('ProfileViewTracker: No authenticated user');
        return;
      }

      if (!profileUserId) {
        debugLogger.debug('ProfileViewTracker: No profile user ID provided');
        return;
      }

      if (!isValidUUID(profileUserId)) {
        debugLogger.error('ProfileViewTracker: Invalid profile user ID format:', profileUserId);
        return;
      }

      if (user.id === profileUserId) {
        debugLogger.debug('ProfileViewTracker: User viewing own profile, skipping tracking');
        return;
      }

      debugLogger.debug('ProfileViewTracker: Attempting to track view', {
        viewer: user.id,
        viewed: profileUserId
      });

      try {
        const recorded = await recordProfileView(profileUserId);
        if (recorded) {
          debugLogger.debug('ProfileViewTracker', 'Profile view tracked successfully', {
            viewer: user.id,
            viewed: profileUserId
          });
        } else {
          debugLogger.debug('ProfileViewTracker: View not recorded (possibly duplicate or error)');
        }
      } catch (error) {
        debugLogger.error('ProfileViewTracker', 'Failed to track profile view:', error);
      }
    };

    // Delay para evitar registrar visualizações acidentais
    const timer = setTimeout(trackView, 2000);
    
    return () => clearTimeout(timer);
  }, [user?.id, profileUserId, recordProfileView]);
};
